import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mountain, ArrowLeft, Fingerprint } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { passkeySupported } from "../lib/passkey";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1585094500201-1a2655fc8241?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxlcGljJTIwaGlraW5nJTIwbW91bnRhaW4lMjBzdW5yaXNlfGVufDB8fHx8MTc3Nzc2OTk0OHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
    const { login, loginWithPasskey } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const [showPasskey, setShowPasskey] = useState(false);

    useEffect(() => {
        setShowPasskey(passkeySupported());
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/feed");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handlePasskey = async () => {
        setPasskeyLoading(true);
        try {
            await loginWithPasskey(email);
            toast.success("Welcome back");
            navigate("/feed");
        } catch (err) {
            // User-cancelled WebAuthn prompts throw NotAllowedError / AbortError.
            if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
                // silent — the user dismissed the native sheet
            } else {
                toast.error(formatApiError(err) || "Couldn't sign in with a passkey.");
            }
        } finally {
            setPasskeyLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-alpine-mist">
            <div className="hidden lg:block relative">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-midnight-pine/50" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-sm font-bold">
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan mb-3">
                            Welcome back
                        </div>
                        <h2 className="font-display font-black text-5xl leading-[0.95] tracking-tighter">
                            Ready for<br />your next<br />
                            <span className="text-ember-orange">adventure?</span>
                        </h2>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8">
                        <div className="w-9 h-9 rounded-md bg-midnight-pine flex items-center justify-center">
                            <Mountain className="w-5 h-5 text-ember-orange" />
                        </div>
                        <span className="font-display font-black text-xl text-midnight-pine">RealWorld Outdoors</span>
                    </Link>
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight mb-2">
                        Log in
                    </h1>
                    <p className="text-slate-gray mb-8">
                        Or <Link to="/signup" className="text-ember-orange font-semibold hover:underline" data-testid="login-to-signup">create a free account</Link>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2 block">Email</label>
                            <input
                                data-testid="login-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 transition-all"
                                placeholder="you@trailhead.com"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-slate-gray">Password</label>
                                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-semibold text-ember-orange hover:underline">
                                    Forgot?
                                </Link>
                            </div>
                            <input
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={loading}
                            className="w-full py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 transition-all"
                        >
                            {loading ? "Logging in…" : "Log in"}
                        </button>
                    </form>

                    {showPasskey && (
                        <>
                            <div className="flex items-center gap-3 my-5">
                                <div className="h-px flex-1 bg-ridge-line" />
                                <span className="text-[11px] font-bold uppercase tracking-widest text-slate-gray">or</span>
                                <div className="h-px flex-1 bg-ridge-line" />
                            </div>
                            <button
                                type="button"
                                data-testid="login-passkey-btn"
                                onClick={handlePasskey}
                                disabled={passkeyLoading}
                                className="w-full py-3 rounded-md border-2 border-midnight-pine text-midnight-pine font-bold flex items-center justify-center gap-2 hover:bg-midnight-pine hover:text-white active:scale-[0.99] disabled:opacity-60 transition-all"
                            >
                                <Fingerprint className="w-5 h-5" />
                                {passkeyLoading ? "Waiting for passkey…" : "Sign in with a passkey"}
                            </button>
                            <p className="text-[11px] text-slate-gray text-center mt-2">
                                Use Face ID, Touch ID, or your fingerprint.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
